"use client";

import {
  CustomSelect,
  CustomSelectMultiple,
  type CustomSelectOption,
} from "@/components/shared/formElements/CustomSelect";
import { DAY_OPTIONS } from "@/lib/constants/classes";
import {
  PublicListingFilterField,
  PublicListingFiltersModal,
} from "@/views/shared/public-listing";

export interface ClassesPublicFiltersDraft {
  season: string | null;
  location: string | null;
  teacher: string | null;
  days: string[];
}

export const EMPTY_CLASSES_PUBLIC_FILTERS_DRAFT: ClassesPublicFiltersDraft = {
  season: null,
  location: null,
  teacher: null,
  days: [],
};

export function countClassesPublicFilters(
  filters: ClassesPublicFiltersDraft,
): number {
  let count = 0;
  if (filters.season) count += 1;
  if (filters.location) count += 1;
  if (filters.teacher) count += 1;
  if (filters.days.length > 0) count += 1;
  return count;
}

export function classesPublicFiltersEqual(
  a: ClassesPublicFiltersDraft,
  b: ClassesPublicFiltersDraft,
): boolean {
  return (
    a.season === b.season &&
    a.location === b.location &&
    a.teacher === b.teacher &&
    a.days.length === b.days.length &&
    a.days.every((day, index) => day === b.days[index])
  );
}

interface ClassesPublicFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  draft: ClassesPublicFiltersDraft;
  onDraftChange: (next: ClassesPublicFiltersDraft) => void;
  onApply: () => void;
  onClearDraft: () => void;
  seasonOptions: CustomSelectOption[];
  locationOptions: string[];
  teacherOptions: CustomSelectOption[];
}

export function ClassesPublicFiltersModal({
  isOpen,
  onClose,
  draft,
  onDraftChange,
  onApply,
  onClearDraft,
  seasonOptions,
  locationOptions,
  teacherOptions,
}: ClassesPublicFiltersModalProps) {
  return (
    <PublicListingFiltersModal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter classes"
      onApply={onApply}
      onClearDraft={onClearDraft}
      hasDraftCriteria={countClassesPublicFilters(draft) > 0}
      testId="classes-public-filters-modal"
      applyTestId="classes-public-filters-apply"
    >
      {seasonOptions.length > 1 ? (
        <PublicListingFilterField label="Season">
          <CustomSelect
            placeholder="All seasons"
            value={draft.season ?? ""}
            onChange={(val) =>
              onDraftChange({
                ...draft,
                season: val === "" ? null : val,
              })
            }
            options={[
              { label: "All seasons", value: "" },
              ...seasonOptions,
            ]}
          />
        </PublicListingFilterField>
      ) : null}

      {locationOptions.length > 1 ? (
        <PublicListingFilterField label="Location">
          <CustomSelect
            placeholder="All locations"
            value={draft.location ?? ""}
            onChange={(val) =>
              onDraftChange({
                ...draft,
                location: val === "" ? null : val,
              })
            }
            options={[
              { label: "All locations", value: "" },
              ...locationOptions.map((name) => ({
                label: name,
                value: name,
              })),
            ]}
          />
        </PublicListingFilterField>
      ) : null}

      {teacherOptions.length > 1 ? (
        <PublicListingFilterField label="Instructor">
          <CustomSelect
            placeholder="All instructors"
            value={draft.teacher ?? ""}
            onChange={(val) =>
              onDraftChange({
                ...draft,
                teacher: val === "" ? null : val,
              })
            }
            options={[
              { label: "All instructors", value: "" },
              ...teacherOptions,
            ]}
          />
        </PublicListingFilterField>
      ) : null}

      <PublicListingFilterField label="Days">
        <CustomSelectMultiple
          options={[...DAY_OPTIONS]}
          selected={draft.days}
          placeholder="All days"
          onChange={(days) => onDraftChange({ ...draft, days })}
          dropdownHeight="max-content"
        />
      </PublicListingFilterField>
    </PublicListingFiltersModal>
  );
}
